import { z } from "zod";
import { trimmedString } from "./primitives";

const boundedString = (max: number) => trimmedString.pipe(z.string().max(max));
const isoDateTime = boundedString(64).refine(
  (value) => !Number.isNaN(Date.parse(value)),
  "Value must be a valid ISO 8601 datetime."
);

export const rescueStatusSchema = z.enum(["detected", "active", "saved", "lost"]);

export const rescueEvidenceSchema = z.object({
  id: trimmedString,
  occurredAt: trimmedString,
  channel: z.enum(["email", "call", "support", "slack", "product", "billing"]),
  title: trimmedString,
  excerpt: trimmedString,
  sourceLabel: trimmedString,
  sourceType: z.enum(["email", "call", "support", "slack", "product", "billing", "crm"]).optional(),
  sourceId: trimmedString.optional(),
  sourceUrl: z.string().url().optional(),
});

export const rescueSignalSchema = z.object({
  key: trimmedString,
  label: trimmedString,
  value: trimmedString,
  severity: z.enum(["info", "warning", "critical"]),
});

export const rescuePlanStepSchema = z.object({
  id: trimmedString,
  title: trimmedString,
  description: trimmedString,
  owner: trimmedString,
  timing: trimmedString,
});

export const rescueCommitmentStatusSchema = z.enum(["at_risk", "verified", "fulfilled", "broken"]);

export const rescueCommitmentSchema = z.object({
  id: boundedString(200),
  statement: boundedString(1_000),
  owner: boundedString(200).nullable(),
  dueAt: isoDateTime.nullable(),
  status: rescueCommitmentStatusSchema,
  confidence: z.number().min(0).max(1),
  evidenceIds: z.array(boundedString(200)).min(1).max(20),
});

export const rescueCaseSchema = z.object({
  id: z.string().uuid(),
  customerId: z.string().uuid(),
  customerName: trimmedString,
  customerEmail: z.string().email().nullable(),
  sourceThreadId: z.string().uuid().nullable(),
  title: trimmedString,
  promise: trimmedString,
  narrative: trimmedString,
  annualValueCents: z.number().int().nonnegative(),
  currency: z.string().length(3),
  renewalAt: trimmedString,
  riskScore: z.number().int().min(0).max(100),
  status: rescueStatusSchema,
  evidence: z.array(rescueEvidenceSchema),
  commitments: z.array(rescueCommitmentSchema),
  signals: z.array(rescueSignalSchema),
  plan: z.array(rescuePlanStepSchema),
  launchedEntityId: z.string().uuid().nullable(),
  launchedAt: trimmedString.nullable(),
  createdAt: trimmedString,
  updatedAt: trimmedString,
});

export const rescueListResponseSchema = z.object({
  cases: z.array(rescueCaseSchema),
  totalValueAtRiskCents: z.number().int().nonnegative(),
  currency: z.string().length(3),
});

export const launchRescueRequestSchema = z.object({
  approved: z.literal(true),
});

export const launchRescueResponseSchema = z.object({
  rescueCase: rescueCaseSchema,
  entityId: z.string().uuid(),
  created: z.boolean(),
});

export const rescueRiskKindSchema = z.enum([
  "broken_promise",
  "renewal_risk",
  "usage_decline",
  "billing_risk",
  "negative_sentiment",
]);

export const rescueDetectionEvidenceSchema = z.object({
  id: boundedString(200),
  occurredAt: isoDateTime,
  channel: z.enum(["email", "call", "support", "slack", "product", "billing"]),
  title: boundedString(200),
  excerpt: boundedString(4_000),
  sourceLabel: boundedString(200),
  sourceType: z.enum(["email", "call", "support", "slack", "product", "billing", "crm"]),
  sourceId: boundedString(500),
  sourceUrl: z.string().url().max(2_000).refine((value) => {
    const protocol = new URL(value).protocol;
    return protocol === "http:" || protocol === "https:";
  }, "sourceUrl must use http or https.").optional(),
}).strict();

export const rescueDetectionSignalSchema = z.object({
  key: z.enum(["promise", "usage", "billing", "renewal", "sentiment", "owner"]),
  label: boundedString(100),
  value: boundedString(200),
  severity: z.enum(["info", "warning", "critical"]),
}).strict();

export const rescueDetectionRequestSchema = z.object({
  version: z.literal("1"),
  bundleId: boundedString(200),
  detectedAt: isoDateTime,
  customerId: z.string().uuid(),
  risk: z.object({
    kind: rescueRiskKindSchema,
    key: boundedString(200),
    title: boundedString(200),
    promise: boundedString(1_000),
  }).strict(),
  commercial: z.object({
    annualValueCents: z.number().int().nonnegative(),
    currency: z.string().trim().length(3).transform((value) => value.toUpperCase()),
    renewalAt: isoDateTime,
  }).strict(),
  evidence: z.array(rescueDetectionEvidenceSchema).min(1).max(50),
  commitments: z.array(rescueCommitmentSchema).min(1).max(20),
  signals: z.array(rescueDetectionSignalSchema).min(1).max(20),
}).strict().superRefine((value, context) => {
  const ids = new Set<string>();
  const sources = new Set<string>();
  value.evidence.forEach((item, index) => {
    const sourceIdentity = `${item.sourceType}:${item.sourceId}`;
    if (ids.has(item.id)) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ["evidence", index, "id"], message: "Evidence IDs must be unique." });
    }
    if (sources.has(sourceIdentity)) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ["evidence", index, "sourceId"], message: "Evidence source identities must be unique." });
    }
    ids.add(item.id);
    sources.add(sourceIdentity);
  });
  const commitmentIds = new Set<string>();
  value.commitments.forEach((item, index) => {
    if (commitmentIds.has(item.id)) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ["commitments", index, "id"], message: "Commitment IDs must be unique." });
    }
    item.evidenceIds.forEach((evidenceId) => {
      if (!ids.has(evidenceId)) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ["commitments", index, "evidenceIds"], message: "Commitments must reference evidence in the same bundle." });
      }
    });
    commitmentIds.add(item.id);
  });
});

export const rescueDetectionResponseSchema = z.object({
  rescueCase: rescueCaseSchema,
  created: z.boolean(),
  evidenceAdded: z.number().int().nonnegative(),
});

export const rescueEvidenceAnalysisRequestSchema = z.object({
  evidenceText: boundedString(8_000).pipe(z.string().min(50, "Paste at least 50 characters of evidence.")),
  sourceLabel: boundedString(200),
  channel: z.enum(["email", "call", "support", "slack"]),
  occurredAt: isoDateTime,
}).strict();

const rescueEvidenceInferenceSchema = z.object({
  responseId: z.string().uuid(),
  confidence: z.number().min(0).max(1),
  fallbackUsed: z.boolean(),
  redactionApplied: z.boolean(),
}).strict();

const rescueEvidenceCitationSchema = z.object({
  evidenceId: boundedString(200),
  quote: boundedString(500),
  supports: z.array(z.enum(["risk_title", "risk_priority", "customer_impact"])).min(1).max(3),
}).strict();

const rescueEvidenceProposalAnalysisSchema = z.object({
  outcome: z.literal("proposal"),
  proposal: rescueDetectionRequestSchema,
  ownerRecommendation: z.object({
    owner: boundedString(200),
    ownerType: z.enum(["team", "role", "agent", "human"]),
    confidence: z.number().min(0).max(1),
    provenance: z.enum(["historical_work", "model"]),
    rationale: boundedString(500),
    evidence: z.array(z.object({
      entityId: boundedString(200),
      title: boundedString(300),
      systems: z.array(boundedString(100)).max(20),
    }).strict()).max(3),
  }).strict(),
  citations: z.array(rescueEvidenceCitationSchema).min(1).max(5),
  inference: rescueEvidenceInferenceSchema,
}).strict();

const rescueEvidenceAbstainedAnalysisSchema = z.object({
  outcome: z.literal("abstained"),
  abstention: z.object({
    reason: boundedString(1_000),
    evidenceConsidered: z.array(z.object({
      evidenceId: boundedString(200),
      sourceLabel: boundedString(200),
      excerpt: boundedString(500),
    }).strict()).min(1).max(5),
  }).strict(),
  inference: rescueEvidenceInferenceSchema,
}).strict();

export const rescueEvidenceAnalysisResponseSchema = z.discriminatedUnion("outcome", [
  rescueEvidenceProposalAnalysisSchema,
  rescueEvidenceAbstainedAnalysisSchema,
]);

export const updateRescueCommitmentRequestSchema = z.object({
  owner: boundedString(200).nullable().optional(),
  status: rescueCommitmentStatusSchema.optional(),
}).strict().refine((value) => value.owner !== undefined || value.status !== undefined, {
  message: "At least one commitment field must be updated.",
});

export const updateRescueCommitmentResponseSchema = z.object({
  rescueCase: rescueCaseSchema,
});

// A durable, requester-scoped approval to create local recovery work. The
// server binds the reviewed plan revision; clients cannot supply execution data.
export const rescueDecisionStatusSchema = z.enum([
  "pending", "completed", "cancelled", "expired", "stale",
]);

export const rescueDecisionSchema = z.object({
  id: z.string().uuid(),
  rescueCaseId: z.string().uuid(),
  status: rescueDecisionStatusSchema,
  question: boundedString(500),
  plan: z.array(rescuePlanStepSchema),
  planRevision: boundedString(200),
  createdAt: isoDateTime,
  expiresAt: isoDateTime,
  resolvedAt: isoDateTime.nullable(),
  entityId: z.string().uuid().nullable(),
}).strict().superRefine((decision, context) => {
  if ((decision.status === "completed") !== (decision.entityId !== null)) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ["entityId"], message: "Only completed decisions have a recovery work result." });
  }
  if ((decision.status === "pending") !== (decision.resolvedAt === null)) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ["resolvedAt"], message: "Resolved decisions require a resolution time." });
  }
});

export const createRescueDecisionRequestSchema = z.object({}).strict();
export const rescueDecisionResponseSchema = z.object({ decision: rescueDecisionSchema }).strict();
export const getRescueDecisionResponseSchema = z.object({ decision: rescueDecisionSchema.nullable() }).strict();
export const respondRescueDecisionRequestSchema = z.object({
  answer: z.enum(["approve", "cancel"]),
}).strict();
export const rescueDecisionErrorCodeSchema = z.enum([
  "decision_expired", "decision_stale", "decision_conflict",
]);
export const rescueDecisionErrorResponseSchema = z.object({
  error: boundedString(500),
  details: z.object({ code: rescueDecisionErrorCodeSchema }).strict(),
}).strict();

export type RescueDecision = z.infer<typeof rescueDecisionSchema>;
export type RescueDecisionStatus = z.infer<typeof rescueDecisionStatusSchema>;
export type CreateRescueDecisionRequest = z.infer<typeof createRescueDecisionRequestSchema>;
export type RescueDecisionResponse = z.infer<typeof rescueDecisionResponseSchema>;
export type GetRescueDecisionResponse = z.infer<typeof getRescueDecisionResponseSchema>;
export type RespondRescueDecisionRequest = z.infer<typeof respondRescueDecisionRequestSchema>;

export type RescueCase = z.infer<typeof rescueCaseSchema>;
export type RescueListResponse = z.infer<typeof rescueListResponseSchema>;
export type LaunchRescueRequest = z.infer<typeof launchRescueRequestSchema>;
export type LaunchRescueResponse = z.infer<typeof launchRescueResponseSchema>;
export type RescueDetectionRequest = z.infer<typeof rescueDetectionRequestSchema>;
export type RescueDetectionResponse = z.infer<typeof rescueDetectionResponseSchema>;
export type RescueEvidenceAnalysisRequest = z.infer<typeof rescueEvidenceAnalysisRequestSchema>;
export type RescueEvidenceAnalysisResponse = z.infer<typeof rescueEvidenceAnalysisResponseSchema>;
export type RescueCommitment = z.infer<typeof rescueCommitmentSchema>;
export type UpdateRescueCommitmentRequest = z.infer<typeof updateRescueCommitmentRequestSchema>;
export type UpdateRescueCommitmentResponse = z.infer<typeof updateRescueCommitmentResponseSchema>;
